import { useMemo, useState } from "react";
import { useEntitlementsForChildren } from "@/hooks/useEntitlements";
import { useAllChildren } from "@/hooks/useAllChildren";
import { useAuth } from "@/hooks/useAuth";
import { WeeklyProgressCard } from "@/components/reports/WeeklyProgressCard";
import { HabitOverviewCard } from "@/components/reports/HabitOverviewCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { EmbeddedCheckoutDialog } from "@/components/EmbeddedCheckoutDialog";
import { PLAN_CONFIG } from "@/lib/plans";
import { BarChart3, TrendingUp, Brain, Sparkles, Check } from "lucide-react";

export default function Reports() {
  const { children, isLoading: childrenLoading } = useAllChildren();
  const { getEntitlementsForChild, hasPlus, isLoading: entitlementsLoading } = useEntitlementsForChildren(children);
  const { user } = useAuth();
  const [showSellDialog, setShowSellDialog] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  return (
    <div className="min-h-full bg-background px-4 pt-safe pb-6">
      <div className="mx-auto max-w-sm space-y-6">
        <h1 className="text-center font-heading text-lg font-semibold tracking-tight text-foreground">
          Utvikling
        </h1>

        {(childrenLoading || entitlementsLoading) ? (
          <div className="space-y-4">
            <Skeleton className="h-48 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        ) : children.length === 0 ? (
          <div className="text-center py-12 space-y-2">
            <p className="text-4xl">📊</p>
            <p className="text-muted-foreground text-sm">
              Legg til barn for å se rapporter.
            </p>
          </div>
        ) : (
          <>
          <Tabs defaultValue={children[0]?.id} className="space-y-4">
            <TabsList className="w-full">
              {children.map((child) => (
                <TabsTrigger key={child.id} value={child.id} className="flex-1 gap-1.5">
                  <span>{child.avatar_emoji}</span>
                  <span>{child.name}</span>
                </TabsTrigger>
              ))}
            </TabsList>
            {children.map((child) => {
              const entitlements = getEntitlementsForChild(child);
              return (
                <TabsContent key={child.id} value={child.id} className="space-y-4">
                  {entitlements.hasPlus ? (
                    <>
                      <WeeklyProgressCard childId={child.id} familyId={child.family_id} childCreatedAt={child.created_at} />
                      <HabitOverviewCard childId={child.id} familyId={child.family_id} childCreatedAt={child.created_at} />
                    </>
                  ) : (
                    <div className="rounded-xl border border-border bg-muted/30 p-5 space-y-3 text-center">
                      <div className="mx-auto w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <BarChart3 className="h-5 w-5 text-primary" />
                      </div>
                      <p className="font-display font-semibold text-sm">
                        Utvikling for {child.avatar_emoji} {child.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Du har kun Flyt Gratis for dette barnet. For å se fremgang, trender og utvikling over tid må du oppgradere til Flyt Pluss.
                      </p>
                      <Button size="sm" className="w-full" onClick={() => setShowSellDialog(true)}>
                        Se hva Flyt Pluss gir
                      </Button>
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>

          {/* Sell dialog + checkout (brukes fra per-barn oppgradering) */}
          <Dialog open={showSellDialog} onOpenChange={setShowSellDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-center text-lg">Flyt Pluss</DialogTitle>
              <DialogDescription className="text-center">
                Struktur og motivasjon – for foreldre som vil følge med.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-3 space-y-1.5 text-center">
                  <TrendingUp className="h-5 w-5 text-primary mx-auto" />
                  <p className="text-xs font-medium">Ukentlige rapporter</p>
                  <p className="text-[11px] text-muted-foreground">Se trender og utvikling over tid</p>
                </div>
                <div className="rounded-lg border border-border p-3 space-y-1.5 text-center">
                  <Brain className="h-5 w-5 text-primary mx-auto" />
                  <p className="text-xs font-medium">AI-kategorisering</p>
                  <p className="text-[11px] text-muted-foreground">Automatisk sortering av oppgaver</p>
                </div>
                <div className="rounded-lg border border-border p-3 space-y-1.5 text-center">
                  <Sparkles className="h-5 w-5 text-primary mx-auto" />
                  <p className="text-xs font-medium">Full historikk</p>
                  <p className="text-[11px] text-muted-foreground">Se utvikling over tid</p>
                </div>
                <div className="rounded-lg border border-border p-3 space-y-1.5 text-center">
                  <BarChart3 className="h-5 w-5 text-primary mx-auto" />
                  <p className="text-xs font-medium">Ukeplanimport</p>
                  <p className="text-[11px] text-muted-foreground">Import direkte fra skolen</p>
                </div>
              </div>
              <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 space-y-1.5">
                <p className="text-xs font-semibold text-center">Tydelig fremgang du kan følge</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-primary shrink-0" /> Full historikk og innsikt</li>
                  <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-primary shrink-0" /> Heatmap, trender og kategorivisning</li>
                  <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-primary shrink-0" /> 14 dager gratis prøveperiode</li>
                </ul>
              </div>
              <div className="text-center space-y-2">
                <Button className="w-full" onClick={() => { setShowSellDialog(false); setCheckoutOpen(true); }}>
                  Prøv gratis i 14 dager – 79 kr/mnd
                </Button>
                <p className="text-[11px] text-muted-foreground">Ingen binding. Avslutt når som helst.</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
          <EmbeddedCheckoutDialog
            open={checkoutOpen}
            onOpenChange={setCheckoutOpen}
            priceId={PLAN_CONFIG.plus.price_id}
            planLabel="Flyt Pluss"
          />
          </>
        )}
      </div>
    </div>
  );
}
